import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import {
  FileText, Printer, Loader2, ClipboardCheck, Stamp, Eye, Trash2, CalendarDays, Undo2, Search, Lock, Unlock,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API } from '../../lib/apiBase';
import { safeJson } from '../../lib/safeJson';
import { STATUS_META } from './faConstants';
import { printAppraisalReport } from './printAppraisal';
import StaffAppraisalForm from './StaffAppraisalForm';
import EvaluationDialog from './EvaluationDialog';
import DirectorApprovalDialog from './DirectorApprovalDialog';
import CycleManager from './CycleManager';

const h = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

function AbsenceDialog({ form, open, onClose, onDone }) {
  const [v, setV] = useState({ cl: '', ml: '', lwp: '', late_marks: '' });
  useEffect(() => { if (open && form) setV({ ...{ cl: '', ml: '', lwp: '', late_marks: '' }, ...(form.hr_absence || {}) }); }, [open, form]);
  if (!form) return null;
  const save = async () => {
    const res = await fetch(`${API}/api/faculty-appraisal/forms/${form.form_id}/hr-absence`, {
      method: 'PUT', headers: h(), credentials: 'include', body: JSON.stringify(v),
    });
    const d = await safeJson(res);
    if (!res.ok) { toast.error(d.detail || 'Failed'); return; }
    toast.success('Records of absence saved');
    onDone?.(); onClose();
  };
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="absence-dialog">
        <DialogHeader>
          <DialogTitle>Records of Absence — {form.employee_name}</DialogTitle>
          <DialogDescription>To be filled by the HR department for A.Y. {form.academic_year}</DialogDescription>
        </DialogHeader>
        {[['cl', 'Casual Leave (CL)'], ['ml', 'Medical Leave (ML)'], ['lwp', 'Leave Without Pay (LWP)'], ['late_marks', 'Total Late Marks in Year']].map(([k, l]) => (
          <div key={k} className="space-y-1">
            <Label className="text-xs">{l}</Label>
            <Input value={v[k] || ''} onChange={e => setV({ ...v, [k]: e.target.value })} data-testid={`abs-${k}`} />
          </div>
        ))}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="abs-save">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function FacultyAppraisalTab({ entityId }) {
  const { user } = useAuth();
  const role = user?.role;
  const isHR = ['hr', 'admin', 'super_admin'].includes(role);
  const isDirector = ['director', 'principal', 'super_admin'].includes(role);
  const isHOD = role === 'hod';

  const [cycles, setCycles] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('action');
  const [q, setQ] = useState('');
  const [cycleFilter, setCycleFilter] = useState('all');
  const [showCycles, setShowCycles] = useState(false);
  const [staffForm, setStaffForm] = useState({ open: false, form: null, readOnly: false });
  const [evalDlg, setEvalDlg] = useState({ open: false, form: null, readOnly: false });
  const [dirDlg, setDirDlg] = useState({ open: false, form: null });
  const [absDlg, setAbsDlg] = useState({ open: false, form: null });
  const [ret, setRet] = useState({ open: false, form: null, note: '' });
  const [unlock, setUnlock] = useState({ open: false, form: null, reason: '' });

  const doUnlock = async () => {
    if (!unlock.reason.trim()) { toast.error('Please state why you are unlocking this evaluation'); return; }
    const res = await fetch(`${API}/api/faculty-appraisal/forms/${unlock.form.form_id}/unlock-evaluation`, {
      method: 'PUT', headers: h(), credentials: 'include', body: JSON.stringify({ reason: unlock.reason.trim() }),
    });
    const d = await safeJson(res);
    if (!res.ok) { toast.error(d.detail || 'Failed to unlock'); return; }
    toast.success('Evaluation unlocked — the HOD can revise Part II');
    setUnlock({ open: false, form: null, reason: '' });
    fetchAll();
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, fRes] = await Promise.all([
        isHR ? fetch(`${API}/api/faculty-appraisal/cycles`, { headers: h(), credentials: 'include' }) : Promise.resolve(null),
        fetch(`${API}/api/faculty-appraisal/forms`, { headers: h(), credentials: 'include' }),
      ]);
      if (cRes && cRes.ok) setCycles(await safeJson(cRes));
      if (fRes.ok) setForms(await safeJson(fRes));
    } catch { toast.error('Failed to load appraisal data'); }
    finally { setLoading(false); }
  }, [isHR]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const isMine = (f) => f.employee_email && user?.email && f.employee_email === user.email;
  const canEvaluate = (f) => f.status === 'submitted' && !isMine(f) && (f.is_hod ? isDirector : (isHOD || isDirector));
  const needsMyAction = (f) =>
    (isMine(f) && ['pending', 'returned'].includes(f.status)) ||
    canEvaluate(f) ||
    (isDirector && f.status === 'hod_evaluated') ||
    (isHR && f.status === 'approved' && !(f.hr_absence || {}).hr_name);

  const openReport = async (f) => {
    const res = await fetch(`${API}/api/faculty-appraisal/forms/${f.form_id}/report`, { headers: h(), credentials: 'include' });
    const d = await safeJson(res);
    if (!res.ok) { toast.error(d.detail || 'Failed to build report'); return; }
    if (!printAppraisalReport(d)) toast.error('Allow pop-ups to print the report');
  };

  const doReturn = async () => {
    const res = await fetch(`${API}/api/faculty-appraisal/forms/${ret.form.form_id}/return`, {
      method: 'PUT', headers: h(), credentials: 'include', body: JSON.stringify({ note: ret.note }),
    });
    const d = await safeJson(res);
    if (!res.ok) { toast.error(d.detail || 'Failed'); return; }
    toast.success('Returned to staff for correction');
    setRet({ open: false, form: null, note: '' });
    fetchAll();
  };

  const removeForm = async (f) => {
    if (!window.confirm(`Remove the appraisal form of ${f.employee_name}?`)) return;
    const res = await fetch(`${API}/api/faculty-appraisal/forms/${f.form_id}`, { method: 'DELETE', headers: h(), credentials: 'include' });
    if (res.ok) { toast.success('Form removed'); fetchAll(); } else toast.error('Failed');
  };

  const filtered = forms
    .filter(f => cycleFilter === 'all' || f.cycle_id === cycleFilter)
    .filter(f => view === 'all' ? true : view === 'mine' ? isMine(f) : needsMyAction(f))
    .filter(f => !q || `${f.employee_name} ${f.department} ${f.designation}`.toLowerCase().includes(q.toLowerCase()));

  const views = [
    { key: 'action', label: 'Needs My Action' },
    { key: 'mine', label: 'My Appraisal' },
    { key: 'all', label: isHR || isDirector ? 'All Staff Forms' : 'All Visible Forms' },
  ];

  return (
    <div className="space-y-4" data-testid="faculty-appraisal-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-slate-900 dark:text-white">SGI Faculty Performance Appraisal</h2>
          <p className="text-sm text-slate-500">
            Staff fills Part I → {isHOD ? 'you score' : 'HOD scores'} Part II (out of 700) → Director gives final approval → HR prints the report.
          </p>
        </div>
        {isHR && (
          <Button variant={showCycles ? 'default' : 'outline'} onClick={() => setShowCycles(v => !v)} data-testid="btn-toggle-cycles">
            <CalendarDays className="w-4 h-4 mr-1" /> {showCycles ? 'Hide' : 'Manage'} Cycles &amp; Eligibility
          </Button>
        )}
      </div>

      {isHR && showCycles && <CycleManager cycles={cycles} onRefresh={fetchAll} entityId={entityId} />}

      <div className="flex flex-wrap items-center gap-2">
        {views.map(v => (
          <Button key={v.key} size="sm" variant={view === v.key ? 'default' : 'outline'}
            onClick={() => setView(v.key)} data-testid={`fa-view-${v.key}`}>
            {v.label}
          </Button>
        ))}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input className="pl-9" placeholder="Search staff or department..." value={q} onChange={e => setQ(e.target.value)} data-testid="fa-search" />
        </div>
        {cycles.length > 0 && (
          <Select value={cycleFilter} onValueChange={setCycleFilter}>
            <SelectTrigger className="w-56" data-testid="fa-cycle-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cycles</SelectItem>
              {cycles.map(c => <SelectItem key={c.cycle_id} value={c.cycle_id}>{c.academic_year} — {c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-400" data-testid="fa-empty">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">
                {view === 'action' ? 'Nothing needs your action right now' :
                  view === 'mine' ? 'No appraisal form has been assigned to you yet' : 'No appraisal forms found'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800">
                  <tr>
                    <th className="p-3 text-left">Faculty</th>
                    <th className="p-3 text-left">A.Y.</th>
                    <th className="p-3 text-left">Stage</th>
                    <th className="p-3 text-left">Part II Score</th>
                    <th className="p-3 text-left">Director</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(f => {
                    const st = STATUS_META[f.status] || { label: f.status, cls: 'bg-slate-100 text-slate-700' };
                    const ev = f.evaluation || {};
                    const dir = f.director || {};
                    return (
                      <tr key={f.form_id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`fa-row-${f.form_id}`}>
                        <td className="p-3">
                          <p className="font-medium text-slate-900 dark:text-white">
                            {f.employee_name}
                            {f.is_hod && <Badge variant="outline" className="ml-2 text-[10px]">HOD</Badge>}
                          </p>
                          <p className="text-xs text-slate-500">{f.department} · {f.designation}</p>
                        </td>
                        <td className="p-3 text-slate-600 dark:text-slate-300">{f.academic_year}</td>
                        <td className="p-3">
                          <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                          {f.evaluation_locked && (
                            <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-medium text-slate-500" data-testid={`fa-locked-${f.form_id}`}>
                              <Lock className="w-3 h-3" /> Locked
                            </span>
                          )}
                          {(f.unlock_history || []).length > 0 && !f.evaluation_locked && (
                            <span className="ml-2 text-[10px] text-amber-600" data-testid={`fa-unlocked-${f.form_id}`}>
                              Reopened by {f.unlock_history[f.unlock_history.length - 1].by}
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          {ev.total_points ? (
                            <span className="font-semibold">{ev.total_points}/{ev.max_points || 700}
                              <Badge className={`ml-2 text-[10px] ${ev.eligible ? 'bg-emerald-500' : 'bg-red-500'}`}>
                                {ev.eligible ? 'Eligible' : 'Below 420'}
                              </Badge>
                            </span>
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="p-3">
                          {dir.decision ? (
                            <Badge className={dir.decision === 'recommended' ? 'bg-emerald-600' : 'bg-red-600'}>
                              {dir.decision === 'recommended' ? 'Recommended' : 'Not Recommended'}
                            </Badge>
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="p-3">
                          <div className="flex justify-end flex-wrap gap-1">
                            {isMine(f) && ['pending', 'returned'].includes(f.status) && (
                              <Button size="sm" onClick={() => setStaffForm({ open: true, form: f, readOnly: false })} data-testid={`btn-fill-${f.form_id}`}>
                                <FileText className="w-3.5 h-3.5 mr-1" /> Fill Form
                              </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => setStaffForm({ open: true, form: f, readOnly: true })} data-testid={`btn-view-${f.form_id}`}>
                              <Eye className="w-3.5 h-3.5 mr-1" /> Staff Form
                            </Button>
                            {canEvaluate(f) && (
                              <Button size="sm" onClick={() => setEvalDlg({ open: true, form: f, readOnly: false })} data-testid={`btn-evaluate-${f.form_id}`}>
                                <ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Evaluate
                              </Button>
                            )}
                            {ev.total_points > 0 && (
                              <Button size="sm" variant="outline" onClick={() => setEvalDlg({ open: true, form: f, readOnly: true })} data-testid={`btn-view-eval-${f.form_id}`}>
                                Part II
                              </Button>
                            )}
                            {isDirector && f.status === 'hod_evaluated' && (
                              <Button size="sm" onClick={() => setDirDlg({ open: true, form: f })} data-testid={`btn-approve-${f.form_id}`}>
                                <Stamp className="w-3.5 h-3.5 mr-1" /> Final Approval
                              </Button>
                            )}
                            {isHR && (
                              <Button size="sm" variant="outline" onClick={() => setAbsDlg({ open: true, form: f })} data-testid={`btn-absence-${f.form_id}`}>
                                Absence
                              </Button>
                            )}
                            {isDirector && f.evaluation_locked && f.status !== 'approved' && (
                              <Button size="sm" variant="outline" className="border-amber-300 text-amber-700"
                                onClick={() => setUnlock({ open: true, form: f, reason: '' })} data-testid={`btn-unlock-${f.form_id}`}>
                                <Unlock className="w-3.5 h-3.5 mr-1" /> Unlock
                              </Button>
                            )}
                            {(isHOD || isDirector || isHR) && ['submitted', 'hod_evaluated'].includes(f.status)
                              && (!f.evaluation_locked || isDirector) && (
                              <Button size="sm" variant="ghost" title="Return to staff for correction"
                                onClick={() => setRet({ open: true, form: f, note: '' })} data-testid={`btn-return-${f.form_id}`}>
                                <Undo2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            {(isHR || isDirector || f.status === 'approved') && (
                              <Button size="sm" variant="outline" onClick={() => openReport(f)} data-testid={`btn-print-${f.form_id}`}>
                                <Printer className="w-3.5 h-3.5 mr-1" /> Print
                              </Button>
                            )}
                            {isHR && (
                              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removeForm(f)} data-testid={`btn-remove-${f.form_id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <StaffAppraisalForm form={staffForm.form} open={staffForm.open} readOnly={staffForm.readOnly}
        onClose={() => setStaffForm({ open: false, form: null, readOnly: false })} onDone={fetchAll} />
      <EvaluationDialog form={evalDlg.form} open={evalDlg.open} readOnly={evalDlg.readOnly}
        onClose={() => setEvalDlg({ open: false, form: null, readOnly: false })} onDone={fetchAll} />
      <DirectorApprovalDialog form={dirDlg.form} open={dirDlg.open}
        onClose={() => setDirDlg({ open: false, form: null })} onDone={fetchAll} />
      <AbsenceDialog form={absDlg.form} open={absDlg.open}
        onClose={() => setAbsDlg({ open: false, form: null })} onDone={fetchAll} />

      <Dialog open={unlock.open} onOpenChange={o => !o && setUnlock({ open: false, form: null, reason: '' })}>
        <DialogContent className="max-w-md" data-testid="unlock-dialog">
          <DialogHeader>
            <DialogTitle>Unlock Part II evaluation</DialogTitle>
            <DialogDescription>
              {unlock.form?.employee_name}'s evaluation was locked when the HOD submitted it
              {unlock.form?.evaluation_locked_by ? ` (${unlock.form.evaluation_locked_by})` : ''}.
              Unlocking lets the HOD score it again. Your name and reason are recorded on the form.
            </DialogDescription>
          </DialogHeader>
          <Textarea rows={3} placeholder="Reason for unlocking (e.g. wrong result percentage entered)"
            value={unlock.reason} onChange={e => setUnlock(u => ({ ...u, reason: e.target.value }))}
            data-testid="unlock-reason-input" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setUnlock({ open: false, form: null, reason: '' })}>Cancel</Button>
            <Button onClick={doUnlock} data-testid="unlock-confirm">Unlock Evaluation</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={ret.open} onOpenChange={o => !o && setRet({ open: false, form: null, note: '' })}>
        <DialogContent className="max-w-md" data-testid="return-dialog">
          <DialogHeader>
            <DialogTitle>Return form to staff</DialogTitle>
            <DialogDescription>{ret.form?.employee_name} will be able to edit and resubmit the form.</DialogDescription>
          </DialogHeader>
          <Textarea rows={3} placeholder="What needs correction?" value={ret.note}
            onChange={e => setRet(r => ({ ...r, note: e.target.value }))} data-testid="return-note-input" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRet({ open: false, form: null, note: '' })}>Cancel</Button>
            <Button onClick={doReturn} data-testid="return-confirm">Return Form</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
